import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import IconArrow from '@components/Images/IconArrow';
import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import { titv_show_path } from '@routes';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';
import { getImgixSrc } from '@utils/images';

import EventStreamHeader from './EventStreamHeader';
import { EventStreamPlayerProps } from './interfaces';

const EventStreamPlayer = ({
  inProgressStream,
  latestEpisode,
  mostRecentStream,
  nextEpisode,
}: EventStreamPlayerProps) => {
  const { trackEvent } = useEventTracking();
  const videoId = inProgressStream?.videoId || mostRecentStream?.videoId;

  const handleNextEpisodeClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.TITVNextEvent,
        clickText: nextEpisode.title,
        clickLink: titv_show_path({ guid: nextEpisode.guid }),
      })
    );
  };

  const handleLatestEpisodeClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.TITVLatestEvent,
        clickText: latestEpisode.title,
        clickLink: titv_show_path({ guid: latestEpisode.guid }),
      })
    );
  };

  const currentStream = () => {
    if (!videoId) {
      return (
        <div className="relative mx-auto aspect-[16/9] w-full max-w-[1728px]">
          <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
            <LazyImgixImage
              src={getImgixSrc({
                path: 'titv/TITV_generic_placeholder.png',
                isEnvDependent: false,
              })}
              alt="TITV Placeholder"
              className="max-h-full max-w-full object-cover"
            />
          </div>
        </div>
      );
    }
    if (inProgressStream && inProgressStream?.videoId) {
      return (
        <div
          className="relative mx-auto aspect-[16/9] w-full max-w-[1728px]"
          data-testid="titv-live-stream"
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&mute=0&playsinline=1&modestbranding=1&rel=0&showinfo=0`}
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            title="Live TITV Stream"
            className="absolute left-0 top-0 h-full w-full border-0"
          />
        </div>
      );
    } else if (mostRecentStream && mostRecentStream?.videoId) {
      return (
        <div className="px-0 sm:px-28" data-testid="titv-most-recent-stream">
          <div className="m-auto max-w-container-lg">
            <div className="flex flex-wrap items-start gap-12 sm:flex-nowrap sm:gap-32 md:gap-56">
              <div className="relative aspect-[16/9] w-full max-w-full flex-shrink-0 sm:max-w-[50%] lg:max-w-[747px]">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&mute=0&playsinline=1`}
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  title="Most Recent TITV Stream"
                  className="absolute left-0 top-0 h-full w-full border-0"
                />
              </div>
              <div
                className={clsx(
                  'flex flex-col px-28 sm:pl-0',
                  !nextEpisode ? 'pb-40 sm:pb-56' : 'w-full justify-between self-stretch'
                )}
              >
                <div>
                  <EventStreamHeader
                    as={nextEpisode?.guid ? 'h1' : 'h2'}
                    title={mostRecentStream.title}
                  />
                  <time
                    className="font-suisse-screen text-disclosure-sm text-grayscale-charcoal"
                    dateTime={mostRecentStream.datetime}
                  >
                    {mostRecentStream.date}
                  </time>
                </div>
                {nextEpisode?.guid && (
                  <div className="my-12 w-11/12 self-end text-right md:mb-0 md:mt-72">
                    <a
                      className="group block font-suisse-screen text-caption-md text-grayscale-charcoal transition hover:text-primary-magenta dark:hover:text-primary-pink"
                      href={titv_show_path({ guid: nextEpisode?.guid })}
                      onClickCapture={handleNextEpisodeClick}
                    >
                      <h3 className="m-0 flex flex-nowrap items-center justify-end font-suisse-intl-mono text-disclosure-md text-primary-magenta">
                        Next Episode
                        <IconArrow className="mb-4 ml-24 h-16 flex-shrink-0 rotate-180 self-end text-primary-magenta transition-transform duration-200 group-hover:translate-x-2" />
                      </h3>
                      <span className="mr-[43px] block">{nextEpisode?.title}</span>
                    </a>
                    {latestEpisode && (
                      <h4 className="mb-0 mt-16">
                        <a
                          className="group flex flex-nowrap items-center justify-end font-suisse-intl-mono text-disclosure-md text-primary-magenta dark:text-primary-pink"
                          href={titv_show_path({ guid: latestEpisode?.guid })}
                          onClickCapture={handleLatestEpisodeClick}
                        >
                          <span>Latest Episode</span>
                          <IconArrow className="mb-4 ml-24 h-16 flex-shrink-0 rotate-180 self-end text-primary-magenta transition-transform duration-200 group-hover:translate-x-2" />
                        </a>
                      </h4>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mx-28 border-0 border-b border-solid pt-0 sm:mx-0 sm:pt-56" />
          </div>
        </div>
      );
    }
  };

  const impressionElement = mostRecentStream
    ? ImpressionElement.TITVMostRecentStream
    : ImpressionElement.TITVLiveStream;

  return (
    <InView
      triggerOnce={true}
      onChange={(inView) => {
        inView &&
          trackEvent(
            impression({
              impressionElement: impressionElement,
              context: inProgressStream ? inProgressStream?.videoId : mostRecentStream?.videoId,
            })
          );
      }}
    >
      {currentStream()}
    </InView>
  );
};

export default EventStreamPlayer;
